import { ENV } from '@/config/env';
import type { TeamMember, WeeklySchedule } from '@/types';

interface TeamAnalytics {
  totalMembers: number;
  activeMembers: number;
  totalJobsCompleted: number;
  averageTeamRating: number;
  totalRevenue: number;
  memberPerformance: Array<{
    memberId: string;
    name: string;
    jobsCompleted: number;
    revenue: number;
    rating: number;
  }>;
}

class TeamService {
  private baseUrl = ENV.API_BASE_URL;
  private defaultAvailability: WeeklySchedule = {
    monday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    tuesday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    wednesday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    thursday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    friday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    saturday: { available: false, startTime: '10:00', endTime: '16:00', timeSlotDuration: 60, bufferTime: 15 },
    sunday: { available: false, startTime: '10:00', endTime: '16:00', timeSlotDuration: 60, bufferTime: 15 }
  };

  // Get team members
  async getTeamMembers(providerId: string): Promise<TeamMember[]> {
    const url = new URL(`${this.baseUrl}/api/v1/auth/company/members`);
    if (providerId) {
      url.searchParams.set('companyId', providerId);
    }

    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': providerId,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team members');
    }

    const data = await response.json();
    const members = data.data || data;

    return (members || []).map((member: any) => {
      const nameParts = (member.name || '').trim().split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ');
      const email = member.email || '';
      const fallbackFirstName = firstName || email.split('@')[0] || 'Member';
      const role = (member.role || 'TECHNICIAN').toString().toUpperCase();
      const joinedDate = member.joinedDate || member.joined_date;
      const parsedJoinedDate = joinedDate ? new Date(joinedDate) : new Date();

      return {
        id: member.id,
        providerId: member.providerId || member.provider_id || providerId,
        userId: member.userId || member.user_id || '',
        firstName: member.firstName || member.first_name || fallbackFirstName,
        lastName: member.lastName || member.last_name || lastName,
        email,
        phone: member.phone || '',
        role,
        skills: Array.isArray(member.skills) ? member.skills : [],
        certifications: Array.isArray(member.certifications) ? member.certifications : [],
        hourlyRate: member.hourlyRate || member.hourly_rate || 0,
        availability: member.availability || this.defaultAvailability,
        isActive: member.isActive ?? member.status === 'active',
        hireDate: parsedJoinedDate,
        createdAt: parsedJoinedDate,
        updatedAt: parsedJoinedDate
      } as TeamMember;
    });
  }

  // Get team analytics
  async getTeamAnalytics(providerId: string): Promise<TeamAnalytics> {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/analytics`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': providerId,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team analytics');
    }

    const data = await response.json();
    return data.data || data;
  }

  // Add team member
  async addTeamMember(providerId: string, memberData: Omit<TeamMember, 'id' | 'providerId' | 'createdAt' | 'updatedAt'>) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': providerId,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(memberData)
    });

    if (!response.ok) {
      throw new Error('Failed to add team member');
    }

    return response.json();
  }

  // Update team member
  async updateTeamMember(memberId: string, updates: Partial<TeamMember>) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members/${memberId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Failed to update team member');
    }

    return response.json();
  }

  // Remove team member
  async removeTeamMember(memberId: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to remove team member');
    }

    return response.json();
  }

  // Get team schedule
  async getTeamSchedule(providerId: string, date: string, viewMode: 'week' | 'day') {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/schedule?date=${date}&view=${viewMode}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': providerId,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team schedule');
    }

    const data = await response.json();
    return data.data || data;
  }
}

export const teamService = new TeamService();

// Team Scheduling Service
class TeamSchedulingService {
  private baseUrl = ENV.API_BASE_URL;

  // Assign job to team member
  async assignJobToMember(providerId: string, assignmentData: {
    memberId: string;
    jobId: string;
    scheduledDate: Date;
    scheduledHour: number;
    estimatedDuration: number;
  }) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/assignments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': providerId,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(assignmentData)
    });

    if (!response.ok) {
      throw new Error('Failed to assign job');
    }

    return response.json();
  }

  // Get team workload
  async getTeamWorkload(providerId: string, startDate: Date, endDate: Date) {
    const response = await fetch(
      `${this.baseUrl}/api/v1/auth/company/workload?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-User-ID': providerId,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch team workload');
    }

    const data = await response.json();
    return data.data || data;
  }
}

export const teamSchedulingService = new TeamSchedulingService();
